import type React from "react";
import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../store";
import {
  selectTruckGroupById,
  selectTruckGroupsLoading,
  selectTruckGroupsError,
  fetchTruckGroups,
  updateTruckGroup,
} from "../store/features/truckGroupSlice";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import {
  createTruckGroupApi,
  updateTruckGroupApi,
  deleteTruckGroupApi,
} from "../api/truckGroupsApi";
import { getErrorMessage } from "../utils/errorHandling";
import { selectTrucksLoading, selectTrucksError } from "../store/features/truckSlice";
import CreateTruckGroupForm from "../components/trucks/CreateTruckGroupForm";
import TruckTable from "../components/trucks/TruckTable";
import { fetchCities } from "../store/features/citySlice";
import { fetchAllTrucks } from "../store/features/allTrucksSlice";
import TruckGroupAboutModal from "../components/trucks/TruckGroupAboutModal";
import TruckGroupsPageHeader from "../components/trucks/TruckGroupsPageHeader";
import ConfirmationDialog from "../components/common/ConfirmationDialog";
import {
  fetchTrucksInGroup,
  selectTrucksForGroup,
  selectTrucksInGroupLoading,
  selectTrucksInGroupError,
  selectTrucksInGroupPaginationForGroup,
} from "../store/features/trucksInGroupSlice";
import { setCurrentPage } from "../store/features/appStateSlice";
import { useTranslation as useI18nextTranslation } from "react-i18next";
import { selectAllTrucks } from "../store/features/allTrucksSlice";
import { isUserAdmin } from "../types/user";

type TranslationKeys = keyof typeof translations.en;

const TruckGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { groupId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.user);
  const { i18n } = useI18nextTranslation();

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get("pageSize")) || 50);
  const PAGE_SIZE = 50;
  const [groupsError, setGroupsError] = useState<string | null>(null);

  // Selectors
  const { cities } = useSelector((state: RootState) => state.cities);
  const groupsLoading = useSelector(selectTruckGroupsLoading);
  const groupsErrorSelector = useSelector(selectTruckGroupsError);
  const trucks = useSelector(selectAllTrucks);
  const trucksLoading = useSelector(selectTrucksLoading);
  const trucksError = useSelector(selectTrucksError);
  const trucksInGroup = useSelector((state: RootState) =>
    groupId ? selectTrucksForGroup(state, groupId) : [],
  );
  const trucksInGroupLoading = useSelector((state: RootState) =>
    groupId ? selectTrucksInGroupLoading(state, groupId) : false,
  );
  const trucksInGroupError = useSelector(selectTrucksInGroupError);
  const pagination = useSelector((state: RootState) =>
    groupId ? selectTrucksInGroupPaginationForGroup(state, groupId) : null,
  );

  // Get current group
  const currentGroup = useSelector((state: RootState) =>
    selectTruckGroupById(state, groupId || ""),
  );

  // Effects
  useEffect(() => {
    dispatch(fetchCities());
    dispatch(fetchAllTrucks());
  }, [dispatch]);

  useEffect(() => {
    if (groupId) {
      const isEditMode = location.pathname.endsWith("/edit");
      setIsEditing(isEditMode);
    }
  }, [groupId, location.pathname]);

  useEffect(() => {
    if (groupId && !isEditing) {
      dispatch(fetchTrucksInGroup({ groupId, page, pageSize }));
    }
  }, [dispatch, groupId, isEditing, page, pageSize]);

  useEffect(() => {
    if (groupId) {
      dispatch(setCurrentPage({ page: "truckGroup", metadata: { id: groupId } }));
    }
  }, [dispatch, groupId]);

  const handlePageChange = (newPagination: { page: number; pageSize: number }) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
    setPage(newPagination.page);
    setPageSize(newPagination.pageSize);
  };

  // Handlers
  const handleEdit = () => {
    if (groupId) {
      navigate(`/trucks/groups/${groupId}/edit`);
    }
  };

  const handleCancel = () => {
    if (groupId) {
      navigate(`/trucks/groups/${groupId}`);
    }
  };

  const handleSubmit = async (groupData: {
    name: string;
    truckIds: string[];
    editorUserIds: string[];
  }) => {
    try {
      if (isEditing && groupId) {
        const updatedGroup = await updateTruckGroupApi(groupId, groupData);
        dispatch(updateTruckGroup(updatedGroup));
        setIsEditing(false);
        navigate(`/trucks/groups/${groupId}`);
      } else {
        const newGroup = await createTruckGroupApi(groupData);
        await dispatch(fetchTruckGroups()).unwrap();
        navigate(`/trucks/groups/${newGroup._id}`);
      }
    } catch (err: any) {
      console.error("Failed to save truck group:", err);
      const errorMessage = getErrorMessage(err, t, "updateTruckGroup");
      // Set the error in the form component
      setGroupsError(errorMessage);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupId) return;
    try {
      await deleteTruckGroupApi(groupId);
      navigate("/trucks/groups");
    } catch (err: any) {
      console.error("Failed to delete truck group:", err);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Loading and error states
  if (groupsLoading || trucksLoading || trucksInGroupLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (groupsErrorSelector || trucksError || trucksInGroupError) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error">
          {groupsErrorSelector || trucksError || trucksInGroupError}
        </Typography>
      </Box>
    );
  }

  const canEdit =
    currentGroup?.user?._id === user?._id ||
    isUserAdmin(user) ||
    (!!user?._id && !!currentGroup?.editorUserIds.includes(user?._id));

  const canDelete = currentGroup?.user?._id === user?._id || isUserAdmin(user);

  // Render
  return (
    <Box>
      <TruckGroupsPageHeader
        currentGroup={currentGroup || undefined}
        isEditing={isEditing}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onInfoClick={() => setInfoDialogOpen(true)}
        canEdit={canEdit}
        canDelete={canDelete}
      />
      {groupId && !isEditing && (
        <Box>
          <TruckTable
            trucks={trucksInGroup}
            cities={cities}
            onSearch={async () => {
              // No search functionality needed for group view
            }}
            showSearch={false}
            pagination={
              pagination || {
                page: 1,
                pageSize: pageSize,
                total: 0,
                totalPages: 1,
                sortBy: "",
                sortOrder: "asc",
              }
            }
            onPaginationChange={handlePageChange}
          />
        </Box>
      )}
      {isEditing && (
        <Box sx={{ mt: 4 }}>
          <CreateTruckGroupForm
            onSubmit={handleSubmit}
            error={groupsError}
            initialData={
              currentGroup
                ? {
                    name: currentGroup.name,
                    truckIds: currentGroup.truckIds,
                    editorUserIds: currentGroup.editorUserIds,
                  }
                : undefined
            }
            onCancel={handleCancel}
            cities={cities}
            trucks={trucks}
          />
        </Box>
      )}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Truck Group")}
        message={t("Are you sure you want to delete this truck group?")}
        confirmButtonText={t("Delete")}
      />

      <TruckGroupAboutModal
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        currentGroup={currentGroup || undefined}
      />
    </Box>
  );
};

export default TruckGroupsPage;
